import qs from 'querystring'
import type { ParsedUrlQueryInput } from 'querystring'
import { httpClient } from '@bs/core'
import type {
  BulkAssignPayload,
  CreateProductPayload,
  LinkedProduct,
  Product,
  ProductDraft,
  ProductExportResponse,
  ProductListParams,
  ProductListResponse,
  ProductLocationStock,
  ProductVariant,
  StockLevel,
  VariationGroupItem,
} from '../../../types/product'
import { mapListVariations } from './mapVariations'
import { parseHiddenSpecFields } from './parseHiddenSpecFields'
import { parsePageScore, parsePageScoreAreas } from './parsePageScore'

const base = (storeId: string) => `/cova/stores/${storeId}/products`
const detail = (storeId: string, productId: string) =>
  `/cova/stores/${storeId}/products/${productId}`
const bulkUrl = (storeId: string) => `/cova/stores/${storeId}/products`
const exportUrl = (storeId: string) => `/cova/stores/${storeId}/products/export`
// Manual pantry products are created through the dedicated pantry endpoint
// (be_api PantryProductsView), NOT the /cova collection used by list/detail/update.
const pantryProductsUrl = (storeId: string) => `/pantry/stores/${storeId}/products`
// PUT to toggle the store's direct-to-WooCommerce-DB price sync flag.
// Disabled on create — the manual-product create no longer touches this flag.
// const updatePriceDirectlyUrl = (storeId: string) =>
//   `/stores/${storeId}/update-price-directly-to-woo-db`

function sanitizeVariationData(
  variations: VariationGroupItem[],
): VariationGroupItem[] {
  return variations.map((v) => {
    const sanitized = {
      ...v,
      image: v.image ?? {},
      volume_unit: 'ml',
    }
    if (Array.isArray(sanitized.locations)) {
      sanitized.locations = sanitized.locations.map((loc) => ({
        ...loc,
        image: loc.image ?? {},
        // Specification fields are always mirrored from the variation item into every location
        plant_type: v.plant_type,
        thc_min: v.thc_min,
        thc_max: v.thc_max,
        thc_unit: v.thc_unit,
        cbd_min: v.cbd_min,
        cbd_max: v.cbd_max,
        cbd_unit: v.cbd_unit,
        cbn_min: v.cbn_min,
        cbn_max: v.cbn_max,
        cbn_unit: v.cbn_unit,
        cbg_min: v.cbg_min,
        cbg_max: v.cbg_max,
        cbg_unit: v.cbg_unit,
        terpenes: v.terpenes,
        terpenes_total: v.terpenes_total,
        terpenes_total_unit: v.terpenes_total_unit,
        effects: v.effects,
        flavours: v.flavours,
        bs_custom_specifications: v.bs_custom_specifications,
        bs_inherit_specs: v.bs_inherit_specs,
        volume: v.volume,
        volume_unit: 'ml',
        weight: v.weight,
        cannabis_weight: v.cannabis_weight,
        description: v.description,
        gtin: v.gtin,
        upc: v.upc,
        _custom_product_meta_gtin: v._custom_product_meta_gtin ?? null,
        _custom_product_meta_upc: v._custom_product_meta_upc ?? null,
      }))
    }
    return sanitized
  })
}

function resolveStock(qty: number): StockLevel {
  if (qty <= 0) return 'out'
  return 'in'
}

function mapVariantFromApi(v: Record<string, unknown>): ProductVariant {
  const locations = (v.locations as Record<string, unknown>[] | undefined) ?? []
  const firstLoc = locations[0] as Record<string, unknown> | undefined
  const stockQty = (v.total_available as number) ?? 0
  const price = parseFloat((firstLoc?.regular_price as string) ?? '0') || 0
  const salePrice = firstLoc?.sale_price
    ? parseFloat(firstLoc.sale_price as string)
    : undefined

  const specs = (v.specifications as Record<string, string>) ?? {}

  const cogsRaw = v.cost_of_goods ?? v.cogs
  const cogs = cogsRaw != null ? parseFloat(String(cogsRaw)) : undefined

  return {
    id: String(v._id ?? v.id ?? ''),
    name: String(v.name ?? ''),
    sku: String(v.short_sku ?? ''),
    price,
    salePrice,
    cogs: Number.isFinite(cogs) ? cogs : undefined,
    stock: resolveStock(stockQty),
    stockQty,
    netWeight: specs.net_weight ?? undefined,
    cannabisEquiv: specs.cannabis_equiv ?? undefined,
    hcClass: specs.hc_classification ?? undefined,
    gtin: String(v._custom_product_meta_gtin ?? ''),
    isCurrent: false,
    availability: undefined,
  }
}

function mapLocationStockFromApi(
  quantityMap: Record<
    string,
    {
      stock_quantity?: number
      threshold?: number
      name?: string
      variations?: { stock_quantity?: number; inventory_quantity?: number }[]
    }
  >,
): ProductLocationStock[] {
  return Object.entries(quantityMap).map(([locationId, loc]) => {
    const variationsSum = (loc.variations ?? []).reduce(
      (sum, v) => sum + (v.stock_quantity ?? v.inventory_quantity ?? 0),
      0,
    )
    return {
      locationId,
      locationName: loc.name ?? locationId,
      stockQty: loc.stock_quantity ?? variationsSum,
      threshold: loc.threshold ?? 0,
    }
  })
}

function mapLinkedProductsFromApi(raw: unknown): LinkedProduct[] {
  if (!Array.isArray(raw)) return []
  return (raw as Record<string, unknown>[]).map((item) => ({
    id: String(item.id ?? item._id ?? ''),
    name: String(item.name ?? ''),
    sku: item.sku ? String(item.sku) : undefined,
    price: item.price != null ? parseFloat(String(item.price)) : undefined,
    stock_quantity:
      item.stock_quantity != null ? Number(item.stock_quantity) : undefined,
    images: Array.isArray(item.images)
      ? (item.images as Record<string, unknown>[]).map((img) => ({
          src: String(img.src ?? ''),
        }))
      : undefined,
  }))
}

function mapFromApi(item: Record<string, unknown>): Product {
  const quantity = (item.quantity as Record<string, unknown>) ?? {}
  const stockQty: number = (quantity.total_stock_quantity as number) ?? 0
  const locations = quantity.locations
    ? mapLocationStockFromApi(
        quantity.locations as Record<
          string,
          {
            stock_quantity?: number
            threshold?: number
            name?: string
            variations?: {
              stock_quantity?: number
              inventory_quantity?: number
            }[]
          }
        >,
      )
    : []

  const categories =
    (item.categories as { id: string; name: string; parent?: string }[]) ?? []
  const primaryCat = categories[0]

  const singularImage = item.image as Record<string, unknown> | undefined
  const pluralImages = (item.images as Record<string, unknown>[]) ?? []
  const images: Product['images'] = singularImage?.src
    ? [
        {
          id: String(singularImage.id ?? '0'),
          src: String(singularImage.src),
          alt: String(singularImage.alt ?? ''),
          title: String(singularImage.name ?? ''),
          caption: String(singularImage.caption ?? ''),
          isFeatured: true,
          srcset: (singularImage.srcset as Record<string, string>) ?? undefined,
        },
      ]
    : pluralImages.map((img, idx) => ({
        id: String(img.id ?? idx),
        src: String(img.src ?? ''),
        alt: String(img.alt ?? ''),
        title: String(img.name ?? ''),
        caption: String(img.caption ?? ''),
        isFeatured: idx === 0,
        srcset: (img.srcset as Record<string, string>) ?? undefined,
      }))

  const rawStatus = String(item.bs_status ?? item.status ?? 'draft')
  const bsStatus = (
    rawStatus === 'trash' ? 'archived' : rawStatus
  ) as Product['status']
  const scheduledAt =
    bsStatus === 'scheduled' && item.date_created_gmt
      ? String(item.date_created_gmt)
      : undefined

  return {
    id: String(item.id ?? item._id ?? ''),
    name: String(item.name ?? ''),
    slug: String(item.slug ?? ''),
    sku: String(item.short_sku ?? ''),
    brand: item.brand ? String(item.brand) : undefined,
    categoryId: primaryCat?.id,
    categoryIds: categories.map((c) => String(c.id)),
    categoryName: primaryCat?.name,
    subCategories: categories.slice(1).map((c) => c.name),
    hcClass: undefined,
    price: parseFloat(String(item.price ?? '0')) || 0,
    minPrice:
      item.min_price != null ? parseFloat(String(item.min_price)) : undefined,
    maxPrice:
      item.max_price != null ? parseFloat(String(item.max_price)) : undefined,
    salePrice: item.sale_price
      ? parseFloat(String(item.sale_price))
      : undefined,
    cogs:
      item.cost_of_goods != null
        ? parseFloat(String(item.cost_of_goods))
        : item.cogs != null
          ? parseFloat(String(item.cogs))
          : undefined,
    status: bsStatus,
    stock: resolveStock(stockQty),
    stockQty,
    locationStock: locations,
    variations: mapListVariations(item.variation_data, categories, item.brand, resolveStock),
    mapped: !(item.pos_disconnect_by_category as boolean),
    images,
    shortDescription: String(item.short_description ?? ''),
    longDescription: String(item.description ?? ''),
    tags: ((item.tags as (string | number | { name: string })[]) ?? []).map(
      (t) => {
        if (typeof t === 'number') return t
        if (typeof t === 'string') return t
        return t.name
      },
    ),
    posProductName: String(item.name ?? ''),
    posSku: String(item.short_sku ?? ''),
    posStatus: String(item.pos_status ?? 'syncing'),
    waitingSyncingFromPos: Boolean(item.waiting_syncing_from_pos),
    posIntegration: 'cova',
    updatedAt: String(item.date_modified_gmt ?? ''),
    createdAt: String(item.date_created_gmt ?? ''),
    publishedAt: item.date_published ? String(item.date_published) : undefined,
    date_created_gmt: item.date_created_gmt
      ? String(item.date_created_gmt)
      : undefined,
    scheduledAt,
    upsellIds: Array.isArray(item.upsell_ids)
      ? (item.upsell_ids as string[])
      : [],
    relatedIds: Array.isArray(item.related_ids)
      ? (item.related_ids as string[])
      : [],
    upsellData: mapLinkedProductsFromApi(item.upsell_data),
    relatedData: mapLinkedProductsFromApi(item.related_data),
    seoTitle:
      (item.seo as Record<string, string> | undefined)?.title ?? undefined,
    seoDescription:
      (item.seo as Record<string, string> | undefined)?.meta_description ??
      undefined,
    canonicalUrl:
      (item.seo as Record<string, string> | undefined)?.canonical_url ??
      undefined,
    seoKeyphrase:
      (item.seo as Record<string, string> | undefined)?.focus_keyphrase ??
      '',
    aiCredits: item.ai_credits
      ? {
          used: (item.ai_credits as Record<string, number>).used ?? 0,
          remaining: (item.ai_credits as Record<string, number>).remaining ?? 0,
          total: (item.ai_credits as Record<string, number>).total ?? 0,
        }
      : undefined,
    locationSpecificData:
      item.location_specific_data != null
        ? Boolean(item.location_specific_data)
        : undefined,
    pageScore: parsePageScore(item.page_score),
    pageScoreAreas: parsePageScoreAreas(item.page_score_data),
    hiddenSpecFields: parseHiddenSpecFields(item.bs_hidden_spec_fields),
  }
}

function buildListParams(params: ProductListParams): Record<string, unknown> {
  const p: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sortBy) {
    p.sort_by = params.sortBy
    p.sort = params.sort ?? -1
  }
  if (params.status && params.status !== 'all')
    p.status = params.status === 'archived' ? 'trash' : params.status
  if (params.search) p.search = params.search
  if (params.locationId) p.location_id = params.locationId
  // Arrays serialize as repeated keys (`category_ids=1&category_ids=2`) via
  // `querystring.stringify` — the DRF `ListField` reads them with `getlist`.
  // Comma-joined values would fail the IntegerField child validation. Sorted
  // so the same selection always produces the same request.
  if (params.categoryIds?.length) p.category_ids = [...params.categoryIds].sort()
  if (params.brandIds?.length) p.brands = [...params.brandIds].sort()
  if (params.hcClass) p.hc_class = params.hcClass
  if (params.stockQuantityMin !== undefined)
    p.stock_quantity_min = params.stockQuantityMin
  if (params.stockQuantityMax !== undefined)
    p.stock_quantity_max = params.stockQuantityMax
  // Exact match on the backend — 'syncing' won't include Budsense-linked
  // products (pos_status: 'budsense'), unlike the column's binary display.
  if (params.posStatus) p.pos_status = params.posStatus
  // Arrays -> repeated `page_score_states=` / `page_score_areas=` keys via
  // querystring.stringify; empty lists are omitted.
  if (params.pageScoreStates?.length) p.page_score_states = params.pageScoreStates
  if (params.pageScoreAreas?.length) p.page_score_areas = params.pageScoreAreas
  return p
}

function mapListResponse(raw: Record<string, unknown>): ProductListResponse {
  const items = ((raw.items as Record<string, unknown>[]) ?? []).map(mapFromApi)
  return {
    data: items,
    totalItems: (raw.total_items as number) ?? items.length,
    totalPages: (raw.total_pages as number) ?? 1,
    all: (raw.all as number) ?? items.length,
    published: (raw.published as number) ?? 0,
    draft: (raw.draft as number) ?? 0,
    archived: (raw.trash as number) ?? 0,
    pageScoreNeedsImprovement: parsePageScore(raw.page_score_needs_improvement),
  }
}

// Shared request-body builder for both update() and create(). Keeping a single
// builder guarantees the create request params stay identical to the edit ones.
function buildProductPayload(data: Partial<ProductDraft>): Record<string, unknown> {
  // external_datasource is always sent as 'manual' — once a product has been
  // edited/created from the Pantry UI we treat it as manually-managed, regardless
  // of the prior datasource. Backend (be_libs/utils/cova/products.py) reads this
  // field to decide whether POS sync may overwrite the doc.
  const payload: Record<string, unknown> = {
    pos_status: 'disconnect',
    external_datasource: 'manual',
  }
  if (data.name !== undefined) payload.name = data.name
  if (data.slug !== undefined) payload.slug = data.slug
  if (data.shortDescription !== undefined)
    payload.short_description = data.shortDescription
  if (data.longDescription !== undefined)
    payload.description = data.longDescription
  if (data.status !== undefined)
    payload.bs_status = data.status === 'archived' ? 'trash' : data.status
  if (data.status === 'scheduled' && data.scheduledAt) {
    payload.date_created_gmt = data.scheduledAt
  }
  if (data.images !== undefined) {
    payload.images = data.images.map((img) => ({
      id: img.id,
      src: img.src,
      alt: img.alt ?? '',
      name: img.title ?? '',
      caption: img.caption ?? '',
    }))
  }
  if (data.tags !== undefined) {
    payload.tags = data.tags.map((t) =>
      typeof t === 'number' ? t : { name: String(t) },
    )
  }
  if (data.categories !== undefined) {
    payload.categories = data.categories.map((c) => ({
      id: Number(c.id),
      name: c.name,
      slug: c.slug,
    }))
  } else if (data.categoryIds !== undefined) {
    payload.categories = data.categoryIds.map((id) => ({ id: Number(id) }))
  }
  if (data.brand !== undefined)
    payload.brand = data.brand ? Number(data.brand) : null
  if (data.variationData !== undefined) {
    payload.variation_data = sanitizeVariationData(data.variationData)
  } else if (data.upc !== undefined || data.gtin !== undefined) {
    const fallback: Record<string, unknown> = {}
    if (data.upc !== undefined) fallback._custom_product_meta_upc = data.upc
    if (data.gtin !== undefined) fallback._custom_product_meta_gtin = data.gtin
    payload.variation_data = [fallback]
  }
  // Always send the full `seo` object so a save never drops a key the
  // backend already stores.
  if (
    data.seoTitle !== undefined ||
    data.seoDescription !== undefined ||
    data.canonicalUrl !== undefined ||
    data.seoKeyphrase !== undefined
  ) {
    payload.seo = {
      title: data.seoTitle ?? '',
      meta_description: data.seoDescription ?? '',
      canonical_url: data.canonicalUrl ?? '',
      focus_keyphrase: data.seoKeyphrase ?? '',
    }
  }
  if (data.upsellIds !== undefined) payload.upsell_ids = data.upsellIds
  if (data.relatedIds !== undefined) {
    payload.related_ids = data.relatedIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))
  }
  if (data.locationSpecificData !== undefined)
    payload.location_specific_data = data.locationSpecificData
  if (data.hiddenSpecFields !== undefined)
    payload.bs_hidden_spec_fields = data.hiddenSpecFields
  return payload
}

// Builds the body for the pantry create endpoint (CreateProductSerializer). This is
// intentionally SEPARATE from buildProductPayload (the update body): create nests
// variation_data as variants→locations, requires an int `brand` (0 = none, never
// null) and int-only `tags`.
function buildCreatePayload(data: Partial<ProductDraft>): CreateProductPayload {
  const payload: CreateProductPayload = {
    name: data.name ?? '',
    slug: data.slug ?? '',
    pos_status: 'disconnect',
    external_datasource: 'manual',
    // Single optional brand id; 0 = no brand (backend rejects null).
    brand: data.brand ? Number(data.brand) : 0,
    // Manual pantry products always carry per-warehouse locations; default true.
    location_specific_data: data.locationSpecificData ?? true,
    variation_data: data.createVariationData ?? [],
  }
  if (data.shortDescription !== undefined) payload.short_description = data.shortDescription
  if (data.longDescription !== undefined) payload.description = data.longDescription
  if (data.status !== undefined)
    payload.bs_status = data.status === 'archived' ? 'trash' : data.status
  if (data.status === 'scheduled' && data.scheduledAt) {
    payload.date_created_gmt = data.scheduledAt
  }
  if (data.images !== undefined) {
    payload.images = data.images.map((img) => ({
      title: img.title ?? '',
      caption: img.caption ?? '',
      name: img.title ?? '',
      alt: img.alt ?? '',
      src: img.src,
      srcset: img.srcset ?? {},
    }))
  }
  if (data.categories !== undefined) {
    payload.categories = data.categories.map((c) => ({ id: Number(c.id) }))
  } else if (data.categoryIds !== undefined) {
    payload.categories = data.categoryIds.map((id) => ({ id: Number(id) }))
  }
  // Tags must be existing numeric ids on create; drop any free-text/new tags.
  if (data.tags !== undefined) {
    payload.tags = data.tags
      .map((t) => (typeof t === 'number' ? t : Number(t)))
      .filter((n) => Number.isFinite(n))
  }
  if (data.upsellIds !== undefined) {
    payload.upsell_ids = data.upsellIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))
  }
  if (data.relatedIds !== undefined) {
    payload.related_ids = data.relatedIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))
  }
  if (
    data.seoTitle !== undefined ||
    data.seoDescription !== undefined ||
    data.canonicalUrl !== undefined ||
    data.seoKeyphrase !== undefined
  ) {
    payload.seo = {
      ...(data.seoTitle !== undefined && { title: data.seoTitle }),
      ...(data.seoDescription !== undefined && {
        meta_description: data.seoDescription,
      }),
      ...(data.canonicalUrl !== undefined && {
        canonical_url: data.canonicalUrl,
      }),
      ...(data.seoKeyphrase !== undefined && {
        focus_keyphrase: data.seoKeyphrase,
      }),
    }
  }
  return payload
}

export const covaProductApis = {
  getList: async (
    storeId: string,
    params: ProductListParams,
  ): Promise<ProductListResponse> => {
    const raw = (await httpClient.json.get(
      `${base(storeId)}?${qs.stringify(buildListParams(params) as ParsedUrlQueryInput)}`,
    )) as Record<string, unknown>
    return mapListResponse(raw)
  },

  getDetail: async (storeId: string, productId: string): Promise<Product> => {
    const raw = (await httpClient.json.get(
      detail(storeId, productId),
    )) as Record<string, unknown>
    return mapFromApi(raw)
  },

  getVariationGroup: async (
    storeId: string,
    productId: string,
    includeUnlinkPos?: boolean,
  ): Promise<VariationGroupItem[]> => {
    const raw = (await httpClient.json.get(
      `${detail(storeId, productId)}/variation_group`,
      { params: { include_unlink_pos: includeUnlinkPos ? 'true' : 'false' } },
    )) as { data?: VariationGroupItem[] } | VariationGroupItem[] | null
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    return (raw as { data?: VariationGroupItem[] }).data ?? []
  },

  update: async (
    storeId: string,
    productId: string,
    data: Partial<ProductDraft>,
  ): Promise<Product> => {
    const raw = (await httpClient.json.put(
      detail(storeId, productId),
      buildProductPayload(data),
    )) as Record<string, unknown>
    return mapFromApi(raw)
  },

  // Create a manual pantry product via the dedicated pantry endpoint
  // (POST /pantry/stores/{storeId}/products, be_api PantryProductsView). The body is
  // built by buildCreatePayload (CreateProductSerializer shape) — variants→locations
  // with a `warehouse_id` per location, assembled at the feature layer by
  // buildCreateVariationData. The endpoint returns only { id, variation_ids }; we
  // surface the new parent product id so the editor can navigate to its detail page.
  create: async (
    storeId: string,
    data: Partial<ProductDraft>,
  ): Promise<Product> => {
    // Best-effort: turn off direct-to-WooCommerce-DB price writes before creating a
    // manual product. Non-blocking — swallow any failure and continue the create.
    // Disabled — the create no longer needs to toggle the price-directly flag.
    // await httpClient.json
    //   .put(updatePriceDirectlyUrl(storeId), { update_price_directly_to_woo_db: false })
    //   .catch(() => undefined)
    const raw = (await httpClient.json.post(
      pantryProductsUrl(storeId),
      buildCreatePayload(data),
    )) as { id?: number | string; variation_ids?: number[] }
    return { id: String(raw?.id ?? '') } as Product
  },

  // Triggers a POS resync — PUTs the same detail endpoint as `update()` but with only
  // `external_datasource: 'pos'`, matching the old ConnectDialog "sync" flow exactly.
  // Deliberately not routed through `update()`, which always forces 'manual'.
  syncFromPos: async (storeId: string, productId: string): Promise<Product> => {
    const raw = (await httpClient.json.put(detail(storeId, productId), {
      external_datasource: 'pos',
    })) as Record<string, unknown>
    return mapFromApi(raw)
  },

  bulkUpdate: async (
    storeId: string,
    ids: string[],
    action: string,
  ): Promise<void> => {
    const bsStatus =
      action === 'publish'
        ? 'published'
        : action === 'draft'
          ? 'draft'
          : action === 'archive'
            ? 'trash'
            : action
    await httpClient.json.put(bulkUrl(storeId), { ids, bs_status: bsStatus })
  },

  // Bulk "Map" — assign category/brand to many products in one request. Uses the
  // real bulk endpoint's {store_id, organization_id, product_ids} envelope (see the
  // verified curl) plus the per-product `categories`/`brand` field names.
  bulkAssign: async (
    storeId: string,
    orgId: string,
    productIds: string[],
    payload: BulkAssignPayload,
  ): Promise<void> => {
    await httpClient.json.put(bulkUrl(storeId), {
      store_id: storeId,
      organization_id: orgId,
      // Backend expects numeric product ids; selection ids are strings.
      product_ids: productIds.map((id) => Number(id)),
      // Always sent on bulk assign — once products are edited from Pantry they are
      // treated as manually-managed so POS sync won't overwrite the assignment.
      external_datasource: 'manual',
      ...(payload.categories !== undefined && { categories: payload.categories }),
      ...(payload.brand !== undefined && { brand: payload.brand }),
    })
  },

  exportCsv: async (
    storeId: string,
    params: ProductListParams,
  ): Promise<ProductExportResponse> => {
    // Export operates on the full filtered set, not the current page — send
    // empty page/limit (matches the legacy `...export?page=&limit=&...` call)
    // so the backend decides inline-vs-async by result size.
    const p = buildListParams(params)
    p.page = ''
    p.limit = ''
    if (params.timeZone !== undefined) p.time_zone = params.timeZone
    return httpClient.json.get(
      `${exportUrl(storeId)}?${qs.stringify(p as ParsedUrlQueryInput)}`,
    ) as Promise<ProductExportResponse>
  },

  // Coupon-flow helper: searches Cova products with their `variations_data`
  // populated. The legacy `/cova/stores/<id>/products/variations` endpoint
  // returns products in the richer shape required by the coupon
  // `applied_products` map (`{ type, variations: [{ id }, …] }`). Returns
  // the trimmed shape Loyalty Coupons actually consume — id, name, type, and
  // a flat variation-id list — to keep the surface small.
  searchProductsWithVariations: async (
    storeId: string,
    params: { search: string; locationIds?: (number | string)[] },
  ): Promise<
    Array<{
      id: string
      name: string
      type: 'simple' | 'variable'
      variations: Array<{ id: string }>
    }>
  > => {
    const search = params.search ?? ''
    const locationIds = params.locationIds ?? []
    // The legacy endpoint expects `location_ids[]=…` for each id and a bare
    // `search=…`. Empty `locationIds` is allowed.
    const locQs = locationIds.map((id) => `location_ids[]=${id}`).join('&')
    const url = `${base(storeId)}/variations?${locQs ? `${locQs}&` : ''}search=${encodeURIComponent(search)}`
    const raw = (await httpClient.json.get(url)) as
      | { items?: Record<string, unknown>[] }
      | Record<string, unknown>[]
      | null
    const items: Record<string, unknown>[] = Array.isArray(raw)
      ? raw
      : (raw?.items ?? [])
    return items.map((item) => {
      const rawType = String(item.type ?? 'variable')
      const type: 'simple' | 'variable' =
        rawType === 'simple' ? 'simple' : 'variable'
      const variationsRaw =
        (item.variations_data as Record<string, unknown>[] | undefined) ?? []
      return {
        id: String(item.id ?? item._id ?? ''),
        name: String(item.name ?? ''),
        type,
        variations: variationsRaw.map((v) => ({
          id: String(v.id ?? v._id ?? ''),
        })),
      }
    })
  },

  searchForLinking: async (
    storeId: string,
    excludeProductId: string,
    term: string,
  ): Promise<LinkedProduct[]> => {
    const p: Record<string, unknown> = {
      limit: 100,
      page: 1,
      search: term.trim(),
      sort: 1,
      sort_by: 'date_created_gmt',
      exclude_product_ids: excludeProductId,
    }
    const raw = (await httpClient.json.get(
      `${base(storeId)}?${qs.stringify(p as ParsedUrlQueryInput)}`,
    )) as Record<string, unknown>
    const items = (raw.items as Record<string, unknown>[]) ?? []
    return items.map((item) => {
      const quantity = (item.quantity as Record<string, unknown>) ?? {}
      const singularImage = item.image as Record<string, unknown> | undefined
      const pluralImages = (item.images as Record<string, unknown>[]) ?? []
      const images = singularImage?.src
        ? [{ src: String(singularImage.src) }]
        : pluralImages.map((img) => ({ src: String(img.src ?? '') }))
      return {
        id: String(item.id ?? item._id ?? ''),
        name: String(item.name ?? ''),
        sku: item.short_sku ? String(item.short_sku) : undefined,
        price: item.price != null ? parseFloat(String(item.price)) : undefined,
        stock_quantity: (quantity.total_stock_quantity as number) ?? undefined,
        images: images.length > 0 ? images : undefined,
      }
    })
  },
}
